import { Rows } from "../../interfaces";
import { BmTemplateInternals, BudgetModalTemplateCurrentState } from "./budget-helper-internal";
import { BeforeMainDataSection } from "./sections/before-main-section";
import { AccumulatedCapitalRequiredSection } from "./sections/accumulated-capital-req-section";
import { InvestmentSection } from "./sections/investment-section";
import { ManagementFeesSection } from "./sections/management-fees-section";
import { OperatingExpensesSection } from "./sections/operating-expenses-section";
import { OrganizationExpensesSection } from "./sections/organization-expenses";
import { TotalCapitalRequiredSection } from "./sections/total-capital-req-section";
import { TotalExpensesSection } from "./sections/total-expeses";
import { BudgetModelFormGroup } from "../../../models/budget-model/pojo/bm-form";


/**
 * The budget model is split into sections.
 * Section 1: (before the data):
  blank spacer rows for readability,
  a fixed cell reference to Total Commitment,
  the table headers.
* Section 2: Management fees includes groups of management fees:
  - example:
    - Domestic LPs,
    - Foreign PLs,
  the total Management Fees amount, and internal row for every group, contains items such as:
  - Management Fees %,
  - Discount %,
  - Management fees (calculated),
  - VAT %,
  - VAT,
  - Catch up
* Section 3: Operating expenses includes the total row and internal rows such as:
  - Fund administration
  - Audit
  - K-1 reporting
  - Others
every row has type of expenses such as:
  - Annual,
  - Monthly,
  - Quarterly,
  - Manual,
  which affects calculation methods depending on type.
* Section 4: Organization expenses includes the total row and internal rows such as:
  - Org./liquidation fees
  - FundRasing
every row has type of expenses such as:
  - Annual,
  - Monthly,
  - Quarterly,
  - Manual,
which affects calculation methods depending on type.
* Section 5: Total expenses and Total expenses percents rows.
* Section 6: Total Investment and total investment percent rows.
* Section 7: Total capital required  and total capital required percent rows.
* Section 8: Accumulated capital required and Accumulated capital required percent rows.
*/
export class BudgetExportPreparer {
  private current: BudgetModalTemplateCurrentState;
  private helper: BmTemplateInternals = new BmTemplateInternals();

  getPreparedData(budgetForm: BudgetModelFormGroup): Rows {
    const newData: Rows = [];
    const beforeMainSection = new BeforeMainDataSection(this.helper, budgetForm);
    beforeMainSection.fillRowsBeforeMainData(newData);
    this.current = beforeMainSection.getCurrent();

    // Management fees
    new ManagementFeesSection(budgetForm, this.helper).fillManagementFeesRows(newData, this.current);

    // Operating expenses
    new OperatingExpensesSection(budgetForm, this.helper).fillOperatingExpensesRows(newData, this.current);

    // Organization expenses
    new OrganizationExpensesSection(budgetForm, this.helper).fillOrganizationExpensesRows(newData, this.current);

    // Totals
    new TotalExpensesSection(this.helper).fillTotalExpensesRows(newData, this.current);

    // Empty row
    newData.push(...this.helper.prepareEmptyRowWithDesign(this.current.headersForLogic));
    this.current.rowIndex++;

    // Investment & capital required
    new InvestmentSection(budgetForm, this.helper).fillInvestmentRows(newData, this.current);
    new TotalCapitalRequiredSection(this.helper).fillTotalCapitalRequiredRows(newData, this.current);

    // Empty row
    newData.push(...this.helper.prepareEmptyRowWithDesign(this.current.headersForLogic));
    this.current.rowIndex++;

    // Accumulated capital required rows
    new AccumulatedCapitalRequiredSection(this.helper).fillAccumulatedCapitalRequiredRows(newData, this.current);
    return newData;
  }

  getHeadersForLogic(): string[] {
    return this.current.headersForLogic;
  }

  getHeadersWithOutTotal(): string[] {
    return this.current.headersWithOutTotalTail;
  }
}


